/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 30/11/2025 - 10:10:31
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 30/11/2025
    * - Author          : USER
    * - Modification    : 
**/
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getChats, createChat, getChatMessages, sendMessage, sendMessageFeedback, sendAnonymousMessage, saveAnonymousChat, uploadDocument, streamMessage } from '../../services/api';
import { waitForClerkToken } from '../../services/clerkToken';
import { useLanguage } from '../../contexts/LanguageContext';
import JKBytesLogo from '../Shared/JKBytesLogo';
import ChatGreeting from './ChatGreeting';
import VoiceInput from './VoiceInput';
import VoiceConversation from './VoiceConversation';
import MapPanel, { extractLocationFromText, shouldShowMap } from './MapPanel';
import './Chat.css';
import './ChatGemini.css';

const Chat = () => {
  const { t } = useLanguage();
  const { user, isLoaded } = useUser();
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({}); // Track feedback: { messageId: 'thumbs_up' | 'thumbs_down' }
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  const [collapsedUserMessages, setCollapsedUserMessages] = useState({});

  // Map Panel State
  const [showMapPanel, setShowMapPanel] = useState(false);
  const [mapLocationData, setMapLocationData] = useState(null);
  const [mapSearchQuery, setMapSearchQuery] = useState('');

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const animatedMessageIdsRef = useRef(new Set());
  const userScrolledUpRef = useRef(false); // CHANGED TO REF!
  const initialChatLoadedRef = useRef(false); // Track if we've handled initial chat loading
  const chatCreatedOnLoginRef = useRef(false); // Track if we've created a chat on login
  const streamBufferRef = useRef(''); // Buffer for smooth typing reveal during streaming
  const streamRevealIntervalRef = useRef(null); // Interval for smooth reveal
  const streamDoneRef = useRef(false); // Whether streaming has finished
  const revealIndexRef = useRef(0); // Current reveal position
  const pendingFinalRef = useRef(null); // Store final messages/callback until reveal completes
  const lastSendTimeRef = useRef(0); // Duplicate message prevention
  const lastSendContentRef = useRef(''); // Track last sent content

  // File upload state
  const [uploadingFile, setUploadingFile] = useState(null); // { name, type, status: 'uploading'|'analyzing'|'done' }
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState(0);
  const [attachedFile, setAttachedFile] = useState(null); // { name, type, content }
  const analysisStagesRef = useRef([
    'Uploading document...',
    'Extracting content...',
    'Combobulating data...',
    'Steering through pages...',
    'Cross-referencing...',
    'Distilling insights...',
    'Polishing summary...',
    'Almost there...'
  ]);
  const analysisIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    const messagesDiv = document.querySelector('.chat-messages');
    if (!messagesDiv) return;
    const isAtBottom = messagesDiv.scrollHeight - messagesDiv.scrollTop - messagesDiv.clientHeight < 100;
    userScrolledUpRef.current = !isAtBottom;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSignedInChat = async () => {
      if (!user || !isAuthLoaded || !isSignedIn || chatCreatedOnLoginRef.current) {
        return;
      }

      chatCreatedOnLoginRef.current = true;
      const token = await waitForClerkToken({ retries: 12, delayMs: 250 });

      if (!token || cancelled) {
        chatCreatedOnLoginRef.current = false;
        return;
      }

      await handleUserLogin();
    };

    bootstrapSignedInChat();

    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    return () => {
      cancelled = true;
    };
  }, [user, isAuthLoaded, isSignedIn]);

  useEffect(() => {
    if (!isSignedIn) {
      chatCreatedOnLoginRef.current = false;
      initialChatLoadedRef.current = false;
    }
  }, [isSignedIn]);

  const handleUserLogin = async () => {
    const token = await waitForClerkToken({ retries: 8, delayMs: 250 });

    if (!token) {
      chatCreatedOnLoginRef.current = false;
      console.warn('Clerk session token not ready yet. Waiting before bootstrapping chats.');
      return;
    }

    try {
      const anonymousMessages = JSON.parse(localStorage.getItem('anonymousMessages') || '[]');
      
      if (anonymousMessages.length > 0) {
        console.log('💾 Auto-saving anonymous chat with', anonymousMessages.length, 'messages');

        const firstUserMessage = anonymousMessages.find(m => m.role === 'user')?.content || 'General Chat';
        const generatedTitle = firstUserMessage.substring(0, 50) || 'Preserved Chat';

        try {
          const response = await saveAnonymousChat(anonymousMessages, generatedTitle);

          setChats([response]);
          setCurrentChat(response);
          setSearchParams({ chat: response.url_code });
          setMessages(anonymousMessages);
          
          localStorage.removeItem('anonymousMessages');
          localStorage.removeItem('anonymousMessageCount');
          setAnonymousMessageCount(0);
          
          console.log('✅ Anonymous chat auto-saved with title:', generatedTitle);
        } catch (error) {
          console.error('Failed to auto-save anonymous chat:', error);
          loadChats();
        }
      } else {
        loadChats();
      }
    } catch (error) {
      console.error('Failed to handle user login:', error);
      loadChats();
    }
  };

  // Anonymous Chat: Track message count for guests
  const [anonymousMessageCount, setAnonymousMessageCount] = useState(0);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const SOFT_LIMIT = 50; // Show sign-in prompt
  const HARD_LIMIT = Number.POSITIVE_INFINITY;

  // Load anonymous message count from localStorage
  useEffect(() => {
    if (!user) {
      const count = parseInt(localStorage.getItem('anonymousMessageCount') || '0');
      setAnonymousMessageCount(count);
      if (count >= SOFT_LIMIT) {
        setShowSignInPrompt(true);
      }
    }
  }, [user]);

  // Load anonymous messages from localStorage on mount
  useEffect(() => {
    if (!user) {
      const anonymousMessages = JSON.parse(localStorage.getItem('anonymousMessages') || '[]');
      // Mark ALL loaded messages as animated so they don't replay typing on refresh
      anonymousMessages.forEach(msg => animatedMessageIdsRef.current.add(msg.id));
      setMessages(anonymousMessages);
    }
  }, [user]);

  // Clear anonymous chat on page exit/reload - fresh start every visit
  useEffect(() => {
    if (!user) {
      const clearAnonymousChat = () => {
        localStorage.removeItem('anonymousMessages');
        localStorage.removeItem('anonymousMessageCount');
      };
      window.addEventListener('beforeunload', clearAnonymousChat);
      return () => window.removeEventListener('beforeunload', clearAnonymousChat);
    }
  }, [user]);

  // Focus textarea when chat or messages change (but don't steal focus continuously)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentChat]);

  useEffect(() => {
    const chatCode = searchParams.get('chat');

    // Handle initial load: validate URL chat exists
    if (chats.length > 0 && !initialChatLoadedRef.current) {
      initialChatLoadedRef.current = true;

      if (chatCode) {
        const chat = chats.find(c => c.url_code === chatCode);
        if (chat) {
          loadChat(chat);
        } else {
          // Chat from URL doesn't exist - reload chats to get the new one
          console.log('⚠️ Chat code', chatCode, 'not found. Reloading chats...');
          loadChats().then(() => {
            // After reloading, try to find the chat again
            const updatedChat = chats.find(c => c.url_code === chatCode);
            if (updatedChat) {
              loadChat(updatedChat);
            } else {
              // Still not found, load first chat if available
              if (chats.length > 0) loadChat(chats[0]);
            }
          });
          return;
        }
      } else {
        // No chat in URL, load first chat if available
        if (chats[0]) loadChat(chats[0]);
      }
    }
    // Handle URL changes after initial load
    else if (initialChatLoadedRef.current && chatCode) {
      const chat = chats.find(c => c.url_code === chatCode);
      if (chat && currentChat && currentChat.url_code !== chat.url_code) {
        // User clicked a different chat
        loadChat(chat);
      } else if (!chat && chatCode) {
        // Chat code in URL but not in our list - reload chats to get it
        console.log('🔄 New chat detected, reloading chats...');
        loadChats();
      }
    }
  }, [searchParams, chats]);

  // Scroll to bottom when messages change (but not when user scrolled up)
  useEffect(() => {
    if (messages.length > 0 && !userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const loadChats = async () => {
    try {
      // NUCLEAR FIX: Don't set loading state - just update chats silently
      // This prevents flash of "Loading..." when switching chats
      const data = await getChats();
      // Handle both array and paginated response
      const chatsList = Array.isArray(data) ? data : (data.results || []);
      setChats(chatsList);
    } catch (error) {
      console.error('Failed to load chats:', error);
      setChats([]); // Set empty array on error
    }
  };

  const loadChat = async (chat) => {
    try {
      setLoadingMessages(true);
      setCurrentChat(chat);
      setSearchParams({ chat: chat.url_code });
      // Clear typing/streaming animation state
      setTypingMessageId(null);
      setDisplayedText('');
      stopSmoothReveal();
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      const data = await getChatMessages(chat.url_code);

      // CRITICAL: Mark ALL existing messages as animated so they don't re-type
      data.forEach(msg => animatedMessageIdsRef.current.add(msg.id));
      
      setMessages(data);
      setLoadingMessages(false);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
      setLoadingMessages(false);
    }
  };

  // Smooth typing reveal: reads from streamBufferRef and reveals characters gradually
  const startSmoothReveal = () => {
    // Clear any existing reveal interval
    if (streamRevealIntervalRef.current) {
      clearInterval(streamRevealIntervalRef.current);
    }
    revealIndexRef.current = 0;
    streamBufferRef.current = '';
    streamDoneRef.current = false;
    pendingFinalRef.current = null;

    streamRevealIntervalRef.current = setInterval(() => {
      const buffer = streamBufferRef.current;
      const currentIdx = revealIndexRef.current;

      if (currentIdx < buffer.length) {
        // Ultra-fast adaptive speed: rapid reveal that keeps up with streaming
        const remaining = buffer.length - currentIdx;
        const charsToReveal = remaining > 1000 ? 40 : remaining > 500 ? 25 : remaining > 200 ? 15 : remaining > 50 ? 8 : 4;
        revealIndexRef.current += charsToReveal;
        setDisplayedText(formatMessageContent(buffer.substring(0, revealIndexRef.current)));
        scrollToBottom();
      } else if (streamDoneRef.current) {
        // All content revealed and streaming is done
        clearInterval(streamRevealIntervalRef.current);
        streamRevealIntervalRef.current = null;

        // Now apply the deferred final message swap
        if (pendingFinalRef.current) {
          const { applyFinal } = pendingFinalRef.current;
          pendingFinalRef.current = null;
          applyFinal();
        }

        setTypingMessageId(null);
        setDisplayedText('');
        setSending(false);

        // Autofocus textarea after bot finishes
        setTimeout(() => {
          if (textareaRef.current) textareaRef.current.focus();
        }, 50);
      }
      // If buffer caught up but stream not done, interval keeps running waiting for more
    }, 10);
  };

  const stopSmoothReveal = () => {
    if (streamRevealIntervalRef.current) {
      clearInterval(streamRevealIntervalRef.current);
      streamRevealIntervalRef.current = null;
    }
    pendingFinalRef.current = null;
  };

  const handleSend = async () => {
    if ((!input.trim() && !attachedFile) || sending) return;

    // Duplicate message prevention - ignore identical messages within 2 seconds
    const now = Date.now();
    const trimmedInput = input.trim();
    if (trimmedInput === lastSendContentRef.current && now - lastSendTimeRef.current < 2000 && !attachedFile) {
      console.log('⚠️ Duplicate message blocked');
      return;
    }
    lastSendTimeRef.current = now;
    lastSendContentRef.current = trimmedInput;

    // Build the actual message to send (includes file content if attached)
    const userDisplayText = attachedFile
      ? (trimmedInput || `Analyze this document: ${attachedFile.name}`)
      : trimmedInput;
    const fileContext = attachedFile && attachedFile.content
      ? `\n\n[Attached document: "${attachedFile.name}"]\nDocument content:\n${attachedFile.content}`
      : attachedFile
        ? `\n\n[Attached document: "${attachedFile.name}"]`
        : '';
    const messageToSend = (trimmedInput || `Please analyze and summarize the key contents of the attached document "${attachedFile?.name}".`) + fileContext;

    // Clear input and attached file
    setInput('');
    const currentAttachedFile = attachedFile;
    setAttachedFile(null);

    // ANONYMOUS USER LOGIC
    if (!user) {
      // Check hard limit
      if (anonymousMessageCount >= HARD_LIMIT) {
        alert(`You've reached the ${HARD_LIMIT} message limit. Please sign in to continue chatting!`);
        return;
      }

      setSending(true);

      // Create user message (show display text, send full context to AI)
      const userMessage = {
        id: `anon-user-${Date.now()}`,
        role: 'user',
        content: currentAttachedFile ? `📎 ${currentAttachedFile.name}\n${userDisplayText}` : userDisplayText,
        created_at: new Date().toISOString()
      };

      // Add user message to UI
      const newMessages = [...messages, userMessage];
      setMessages(newMessages);

      // Increment counter
      const newCount = anonymousMessageCount + 1;
      setAnonymousMessageCount(newCount);
      localStorage.setItem('anonymousMessageCount', newCount.toString());

      // Show sign-in prompt after soft limit
      if (newCount >= SOFT_LIMIT) {
        setShowSignInPrompt(true);
      }

      // Keep focus on textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);

      // Get AI response via streaming with smooth typing reveal
      try {
        const chatHistory = messages.map(m => ({ role: m.role, content: m.content }));
        const streamAssistantId = `anon-stream-${Date.now()}`;
        let streamedContent = '';

        // Add placeholder assistant message (shows typing dots until first chunk)
        const streamMsg = { id: streamAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString() };
        setMessages(prev => [...prev, streamMsg]);
        setTypingMessageId(streamAssistantId);
        animatedMessageIdsRef.current.add(streamAssistantId);

        // Start smooth reveal interval
        startSmoothReveal();

        await streamMessage(
          messageToSend,
          'auto',
          chatHistory,
          null,
          // onChunk - feed into buffer for smooth reveal
          (chunk) => {
            streamedContent += chunk;
            streamBufferRef.current = streamedContent;
          },
          // onDone - defer final swap until reveal catches up
          (assistantMsgData) => {
            animatedMessageIdsRef.current.add(assistantMsgData.id);
            const streamContent = streamedContent;
            // Store final swap to apply after reveal finishes
            pendingFinalRef.current = {
              applyFinal: () => {
                const finalMsg = { ...assistantMsgData, content: streamContent };
                const finalMessages = [...newMessages, finalMsg];
                setMessages(finalMessages);
                localStorage.setItem('anonymousMessages', JSON.stringify(finalMessages));
                setSending(false);
                if (streamContent) checkForLocationAndShowMap(userDisplayText, streamContent);
                if (textareaRef.current) textareaRef.current.focus();
              }
            };
            streamDoneRef.current = true;
          },
          // onError
          (error) => {
            console.error('Stream error:', error);
            stopSmoothReveal();
            setMessages(prev => prev.filter(m => m.id !== streamAssistantId));
            setTypingMessageId(null);
            setDisplayedText('');
            setSending(false);
          }
        );
      } catch (error) {
        console.error('Failed to send anonymous message:', error);
        setMessages(messages);
        setSending(false);
        alert('Failed to send message. Please try again.');
      }

      return;
    }

    // AUTHENTICATED USER LOGIC
    // Auto-create chat if none exists (only when user sends first message)
    const draftTitle = deriveDraftTitle(userDisplayText, currentAttachedFile);
    let chatToUse = currentChat;
    if (!chatToUse) {
      console.log('📝 No chat exists, creating one now...');
      try {
        const newChat = await createChat(draftTitle !== 'New Chat' ? draftTitle : null);
        setChats([newChat]);
        setCurrentChat(newChat);
        setSearchParams({ chat: newChat.url_code });
        chatToUse = newChat;
      } catch (error) {
        console.error('Failed to create chat:', error);
        alert('Failed to create chat. Please try again.');
        return;
      }
    }

    const userMessageText = userDisplayText;
    setSending(true);

    // Check if this is the first message (for title generation)
    const isFirstMessage = messages.length === 0;

    if (isFirstMessage && chatToUse?.url_code && draftTitle !== 'New Chat') {
      setChats(prev => prev.map(chat =>
        chat.url_code === chatToUse.url_code ? { ...chat, title: draftTitle } : chat
      ));
      setCurrentChat(prev => (
        prev?.url_code === chatToUse.url_code ? { ...prev, title: draftTitle } : prev
      ));
      window.dispatchEvent(new CustomEvent('chat-title-updated'));
    }

    // Get selected language from localStorage
    const selectedLanguageCode = localStorage.getItem('selectedLanguageCode') || 'en';
    console.log('Sending message with language:', selectedLanguageCode);

    // Optimistically add user message (show file badge if attached)
    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: currentAttachedFile ? `📎 ${currentAttachedFile.name}\n${userMessageText}` : userMessageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    // Keep focus on textarea after sending
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);

    try {
      // Use streaming with smooth typing reveal
      const chatHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const streamAssistantId = `stream-${Date.now()}`;
      let streamedContent = '';
      let realUserMsgData = null;

      // Add placeholder assistant message for streaming (shows typing dots)
      const streamAssistantMsg = {
        id: streamAssistantId,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, streamAssistantMsg]);

      // Set typing to show dots, then smooth reveal
      setTypingMessageId(streamAssistantId);
      animatedMessageIdsRef.current.add(streamAssistantId);

      // Start smooth reveal interval
      startSmoothReveal();

      await streamMessage(
        messageToSend,
        selectedLanguageCode,
        chatHistory,
        chatToUse.url_code,
        // onChunk - feed into buffer for smooth reveal
        (chunk) => {
          streamedContent += chunk;
          streamBufferRef.current = streamedContent;
        },
        // onDone - defer final swap until reveal catches up
        (assistantMsgData) => {
          animatedMessageIdsRef.current.add(assistantMsgData.id);
          const streamContent = streamedContent;
          const savedRealUserMsgData = realUserMsgData;
          // Store final swap to apply after reveal finishes
          pendingFinalRef.current = {
            applyFinal: () => {
              setMessages(prev => {
                // Remove BOTH the temp user msg AND the real user msg (if replaced via onUserMessage)
                // Also remove the streaming placeholder assistant msg
                const realUserId = savedRealUserMsgData ? savedRealUserMsgData.id : null;
                const filtered = prev.filter(m => 
                  m.id !== tempUserMessage.id && 
                  m.id !== streamAssistantId && 
                  (realUserId ? m.id !== realUserId : true)
                );
                const realUser = savedRealUserMsgData || { ...tempUserMessage };
                return [...filtered, realUser, { ...assistantMsgData, content: streamContent }];
              });
              if (streamContent) checkForLocationAndShowMap(userMessageText, streamContent);
              // Reload chats for title update
              loadChats();
              window.dispatchEvent(new CustomEvent('chat-title-updated'));
              if (textareaRef.current) textareaRef.current.focus();
            }
          };
          streamDoneRef.current = true;
        },
        // onError
        (error) => {
          console.error('Stream error:', error);
          stopSmoothReveal();
          setMessages(prev => prev.filter(m => m.id !== streamAssistantId));
          setTypingMessageId(null);
          setDisplayedText('');
        },
        // onUserMessage - replace temp user message with real DB message
        (userMsgData) => {
          realUserMsgData = userMsgData;
          setMessages(prev => prev.map(m =>
            m.id === tempUserMessage.id ? { ...userMsgData } : m
          ));
        },
        // onTitleUpdate - update chat title from backend
        (titleData) => {
          if (titleData && titleData.title) {
            setChats(prev => prev.map(c =>
              c.url_code === chatToUse.url_code ? { ...c, title: titleData.title } : c
            ));
            if (currentChat && currentChat.url_code === chatToUse.url_code) {
              setCurrentChat(prev => ({ ...prev, title: titleData.title }));
            }
            window.dispatchEvent(new CustomEvent('chat-title-updated'));
          }
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      setTypingMessageId(null);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle voice transcription completion
  const handleVoiceTranscription = (transcribedText) => {
    console.log('✅ Voice transcribed:', transcribedText);
    setInput(transcribedText);

    // Auto-focus textarea after transcription
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleClearInput = () => {
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const deriveDraftTitle = (messageText, file = null, maxLength = 60) => {
    const normalized = (messageText || '').replace(/\s+/g, ' ').trim();

    if (normalized) {
      return normalized.slice(0, maxLength).replace(/[\s.,:;-]+$/, '') || 'New Chat';
    }

    if (file?.name) {
      return `Discuss ${file.name}`.slice(0, maxLength);
    }

    return 'New Chat';
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const renderChatNavbar = () => (
    <div className="chat-navbar">
      <div className="chat-navbar-brand">
        <JKBytesLogo size="small" />
      </div>
      <button className="chat-navbar-signin" onClick={() => navigate('/sign-in')}>
        Sign In
      </button>
    </div>
  );

  // Get file type category for upload UI
  const getFileTypeCategory = (file) => {
    if (!file) return 'default';
    const type = file.type || '';
    const name = file.name || '';
    if (type.startsWith('image/')) return 'image';
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return 'doc';
    return 'default';
  };

  const getAnalysisLabel = () => {
    return analysisStagesRef.current[analysisStage % analysisStagesRef.current.length];
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileCategory = getFileTypeCategory(file);
    setUploadingFile({ name: file.name, type: fileCategory, status: 'uploading' });
    setUploadProgress(0);
    setAnalysisStage(0);

    // Start rotating fun analysis labels
    let stageIdx = 0;
    analysisIntervalRef.current = setInterval(() => {
      stageIdx++;
      setAnalysisStage(stageIdx);
      setUploadProgress(prev => Math.min(prev + 8, 90));
    }, 1800);

    try {
      let extractedContent = '';

      // Check if user is authenticated for backend upload
      const isAuthenticated = Boolean(isSignedIn);

      if (isAuthenticated) {
        // Authenticated: upload to backend for processing + ChromaDB storage
        setUploadingFile(prev => ({ ...prev, status: 'analyzing' }));
        const result = await uploadDocument(file);
        extractedContent = result.content_preview || '';
      }

      // Also read text content client-side for txt/md files (works for everyone)
      const textExtensions = ['.txt', '.md', '.csv'];
      const ext = file.name.toLowerCase().split('.').pop();
      if (textExtensions.includes('.' + ext) && !extractedContent) {
        extractedContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(file);
        });
        // Limit to 5000 chars
        if (extractedContent.length > 5000) {
          extractedContent = extractedContent.slice(0, 5000) + '\n...[truncated]';
        }
      }

      // Clear the rotating interval
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
      setUploadProgress(100);

      // Show done state briefly then clear upload indicator
      setUploadingFile(prev => ({ ...prev, status: 'done' }));
      setTimeout(() => {
        setUploadingFile(null);
        setUploadProgress(0);
        setAnalysisStage(0);
      }, 1000);

      // Attach file as a chip in the input area
      setAttachedFile({
        name: file.name,
        type: fileCategory,
        content: extractedContent
      });

      // Focus textarea so user can type a question
      setTimeout(() => {
        if (textareaRef.current) textareaRef.current.focus();
      }, 100);
    } catch (error) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
      console.error('Failed to upload file:', error);
      setUploadingFile(null);
      setUploadProgress(0);
      setAnalysisStage(0);

      // Fallback: try reading client-side for text files
      const ext = file.name.toLowerCase().split('.').pop();
      if (['txt', 'md', 'csv'].includes(ext)) {
        try {
          const text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
          });
          const content = text.length > 5000 ? text.slice(0, 5000) + '\n...[truncated]' : text;
          setAttachedFile({ name: file.name, type: fileCategory, content });
          setTimeout(() => { if (textareaRef.current) textareaRef.current.focus(); }, 100);
        } catch (readErr) {
          alert('Failed to read file. Please try again.');
        }
      } else {
        alert('Failed to upload file. Please sign in to upload PDFs and Word documents.');
      }
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if message should show map - checks BOTH user intent and AI response content
  const checkForLocationAndShowMap = (userText, aiResponseText) => {
    // Check if user explicitly asked for a location
    const userAsked = shouldShowMap(userText);

    // Also check if AI response contains postcodes (meaning it gave location info)
    const hasPostcodeInResponse = aiResponseText && /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i.test(aiResponseText);

    if (userAsked || hasPostcodeInResponse) {
      const locationData = extractLocationFromText(aiResponseText || userText);
      if (locationData && locationData.query) {
        setMapLocationData(locationData);
        setMapSearchQuery(locationData.query);
        setShowMapPanel(true);
      }
    }
  };

  // Handle opening map manually from a message
  const handleShowMapForMessage = (content) => {
    const locationData = extractLocationFromText(content);
    if (locationData) {
      setMapLocationData(locationData);
      setMapSearchQuery(locationData.query || content.substring(0, 100));
      setShowMapPanel(true);
    } else {
      // Just show empty map if no location found
      setMapLocationData(null);
      setMapSearchQuery(content.substring(0, 100));
      setShowMapPanel(true);
    }
  };

  const formatMessageContent = (content) => {
    try {
      if (!content) return '';

      let formatted = String(content);

      // REMOVE SOURCES/CITATIONS SECTION
      formatted = formatted.replace(/\n*(?:Sources?|References?|Citations?|Related Links?|Resources?)[\s\S]*$/gi, '');

      // Convert markdown bold/italic: ***text***, **text**, __text__, *text*
      formatted = formatted.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');
      formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      formatted = formatted.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
      formatted = formatted.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

      // Convert markdown headers to styled headings
      formatted = formatted.replace(/^#{3,6}\s+(.*?)$/gm, '<strong style="display:block;margin:0.5em 0 0.25em;font-size:0.95em;">$1</strong>');
      formatted = formatted.replace(/^##\s+(.*?)$/gm, '<strong style="display:block;margin:0.6em 0 0.3em;font-size:1em;">$1</strong>');
      formatted = formatted.replace(/^#\s+(.*?)$/gm, '<strong style="display:block;margin:0.6em 0 0.3em;font-size:1.05em;">$1</strong>');

      // Convert markdown links [text](url) to clickable links
      formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="url-link">$1</a>');

      // Convert bullet lists (- item or * item), including indented ones
      formatted = formatted.replace(/^(\s*)[\-\*]\s+(.*?)$/gm, (match, indent, text) => {
        const level = Math.min(Math.floor(indent.length / 2), 3);
        const paddingLeft = 1 + level * 1.2;
        return `<span style="display:block;padding-left:${paddingLeft}em;margin:0.15em 0;">&#8226; ${text}</span>`;
      });

      // Convert numbered lists (1. item, 2. item) including indented
      formatted = formatted.replace(/^(\s*)(\d+)\.\s+(.*?)$/gm, (match, indent, num, text) => {
        const level = Math.min(Math.floor(indent.length / 2), 3);
        const paddingLeft = 1 + level * 1.2;
        return `<span style="display:block;padding-left:${paddingLeft}em;margin:0.15em 0;"><strong>${num}.</strong> ${text}</span>`;
      });

      // Clean up excessive blank lines
      formatted = formatted.replace(/\n{3,}/g, '\n\n');
      formatted = formatted.trim();

      // Convert standalone URLs to clickable links (not already in <a> tags)
      const urlPlaceholders = [];
      formatted = formatted.replace(/(?<!["\w/])(https?:\/\/[^\s<]+)/g, (url) => {
        const cleanUrl = url.replace(/[.,;:!?)]+$/, '');
        const placeholder = `__URLPH_${urlPlaceholders.length}__`;
        urlPlaceholders.push(`<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="url-link">${cleanUrl}</a>`);
        return placeholder;
      });

      // Convert newlines to <br>
      formatted = formatted.replace(/\n/g, '<br>');

      // Restore URL placeholders
      urlPlaceholders.forEach((link, index) => {
        formatted = formatted.replace(`__URLPH_${index}__`, link);
      });

      return formatted || content;
    } catch (error) {
      console.error('Error formatting message:', error);
      return String(content);
    }
  };

  const handleNewChat = async () => {
    try {
      const newChat = await createChat();
      setChats([newChat, ...chats]);
      loadChat(newChat);
    } catch (error) {
      console.error('Failed to create chat:', error);
    }
  };

  const handleFeedback = async (messageId, feedbackType) => {
    // If clicking the same feedback type, remove it (toggle off)
    if (messageFeedback[messageId] === feedbackType) {
      setMessageFeedback(prev => {
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
      return;
    }

    // Set new feedback in UI
    setMessageFeedback(prev => ({
      ...prev,
      [messageId]: feedbackType
    }));

    // Only send to backend if authenticated
    if (!user) return;

    const backendRating = feedbackType === 'thumbs_up' ? 'positive' : 'negative';

    try {
      await sendMessageFeedback(messageId, backendRating);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      setMessageFeedback(prev => {
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
    }
  };

  // Guest User View - Not Signed In
  if (!isLoaded || (user && !isAuthLoaded)) {
    return (
      <div className="chat-fullscreen-loader">
        <div className="tuliza-page-loader">
          <img src="/tuliza.jpg" alt="Tuliza" className="tuliza-loader-img" />
        </div>
      </div>
    );
  }

  const handleCopyMessage = async (content) => {
    try {
      // Strip HTML tags for plain text copy
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      await navigator.clipboard.writeText(plainText);
      setCopiedMessageId(content);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleEditMessage = (messageId, content) => {
    setEditingMessageId(messageId);
    setEditedContent(content);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editedContent.trim()) return;

    const editedText = editedContent.trim();
    setEditingMessageId(null);
    setEditedContent('');

    // Find the message index and remove it plus all messages after it (the old AI response)
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;

    // Keep messages before the edited one
    const previousMessages = messages.slice(0, messageIndex);

    // Add the edited user message
    const editedUserMessage = {
      id: `edited-${Date.now()}`,
      role: 'user',
      content: editedText,
      created_at: new Date().toISOString()
    };
    setMessages([...previousMessages, editedUserMessage]);
    setSending(true);

    try {
      if (!user) {
        // Anonymous user - send conversation history for memory
        const chatHistory = previousMessages.map(m => ({ role: m.role, content: m.content }));
        const response = await sendAnonymousMessage(editedText, 'auto', 'flash', chatHistory);
        const updatedMessages = [...previousMessages, editedUserMessage, response.assistant_message];
        setMessages(updatedMessages);
        localStorage.setItem('anonymousMessages', JSON.stringify(updatedMessages));
      } else if (currentChat) {
        // Authenticated user - resend to get new AI response
        const selectedLanguageCode = localStorage.getItem('selectedLanguageCode') || 'en';
        const response = await sendMessage(currentChat.url_code, editedText, selectedLanguageCode);
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== editedUserMessage.id);
          return [...filtered, response.user_message, response.assistant_message];
        });
      }
    } catch (error) {
      console.error('Failed to resend edited message:', error);
      // Restore original messages on error
      setMessages(messages);
    } finally {
      setSending(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  const getMessageCollapseKey = (message) => (
    message?.id || message?.created_at || message?.content || ''
  );

  const isLongUserMessage = (content = '') => {
    const text = String(content || '');
    return text.length > 260 || text.split('\n').length > 5;
  };

  const isUserMessageCollapsed = (message, content = message?.content) => {
    const key = getMessageCollapseKey(message);
    return collapsedUserMessages[key] ?? isLongUserMessage(content);
  };

  const toggleUserMessageCollapse = (message, content = message?.content) => {
    const key = getMessageCollapseKey(message);
    setCollapsedUserMessages(prev => ({
      ...prev,
      [key]: !isUserMessageCollapsed(message, content)
    }));
  };

  const renderUserText = (message, content = message?.content) => {
    const text = String(content || '');
    const isLong = isLongUserMessage(text);
    const isCollapsed = isLong && isUserMessageCollapsed(message, text);

    return (
      <div className="user-message-text-shell">
        <div className={`user-message-body${isCollapsed ? ' collapsed' : ''}`}>
          {text}
        </div>
        {isLong && (
          <button
            type="button"
            className="message-collapse-btn"
            onClick={() => toggleUserMessageCollapse(message, text)}
            aria-expanded={!isCollapsed}
          >
            <span>{isCollapsed ? 'Show more' : 'Show less'}</span>
            <svg className={isCollapsed ? '' : 'expanded'} width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    );
  };

  const isFreshConversation = !loadingMessages && messages.length === 0 && !sending;
  const chatShellClassName = `chat-container${sending ? ' bot-responding' : ''} ${isFreshConversation ? 'chat-empty' : 'chat-active'}`;

  if (!user) {
    return (
      <div className={chatShellClassName}>
        {renderChatNavbar()}

        {/* Chat Messages Area */}
        <div className="chat-messages" onScroll={handleScroll}>
          {loadingMessages ? (
            <div className="loading-messages" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <ChatGreeting onSuggestionClick={handleSuggestionClick} />
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`message-wrapper ${message.role === 'user' ? 'user-message' : 'bot-message'}`}
                >
                  {message.role === 'assistant' && (
                    <div className={`message-avatar bot-avatar tuliza-avatar${message.id === typingMessageId ? ' responding' : ''}`}>
                      <img src="/tuliza.jpg" alt="Tuliza" className="tuliza-avatar-img" />
                    </div>
                  )}

                  {/* Hide bubble when waiting for first chunk, show only avatar with spinner */}
                  {!(message.id === typingMessageId && !displayedText) && (
                  <div className="message-content">
                    <div className={`message-bubble ${message.role === 'user' ? 'user-bubble' : 'bot-bubble'}`}>
                      {message.role === 'assistant' ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: message.id === typingMessageId ? displayedText : formatMessageContent(message.content)
                            }}
                          />
                      ) : editingMessageId === message.id ? (
                        <div className="edit-message-container">
                          <textarea
                            className="edit-message-textarea"
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSaveEdit(message.id);
                              }
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            autoFocus
                          />
                          <div className="edit-message-actions">
                            <button className="edit-cancel-btn" onClick={handleCancelEdit}>
                              Cancel
                            </button>
                            <button className="edit-save-btn" onClick={() => handleSaveEdit(message.id)}>
                              Send
                            </button>
                          </div>
                        </div>
                      ) : (
                        (() => {
                          const hasDoc = message.content && message.content.startsWith('📎 ');
                          if (hasDoc) {
                            const lines = message.content.split('\n');
                            const docName = lines[0].replace('📎 ', '');
                            const rest = lines.slice(1).join('\n').trim();
                            return (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <div className="msg-doc-badge" title={docName}>
                                  <span className="badge-icon">📎</span>
                                  <span className="badge-name">{docName}</span>
                                </div>
                                {rest && <div style={{ flex: 1, minWidth: '150px' }}>{renderUserText(message, rest)}</div>}
                              </div>
                            );
                          }
                          return renderUserText(message);
                        })()
                      )}
                    </div>
                    <div className="message-footer">
                      <div className="message-timestamp">{formatTime(message.created_at)}</div>
                      <button
                        className="copy-btn"
                        onClick={() => handleCopyMessage(message.content)}
                        title="Copy message"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          color: copiedMessageId === message.content ? 'var(--accent-color)' : 'var(--text-tertiary)',
                          transition: 'color 0.2s'
                        }}
                      >
                        {copiedMessageId === message.content ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                      {message.role === 'user' && editingMessageId !== message.id && (
                        <button
                          className="edit-btn"
                          onClick={() => handleEditMessage(message.id, message.content)}
                          title="Edit message"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            color: 'var(--text-tertiary)',
                            transition: 'color 0.2s'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      {message.role === 'assistant' && message.id !== typingMessageId && (
                        <>
                          {(() => {
                            const msgIndex = messages.findIndex(m => m.id === message.id);
                            const prevUserMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;
                            return prevUserMsg && prevUserMsg.role === 'user' && shouldShowMap(prevUserMsg.content);
                          })() && (
                            <button
                              className="feedback-btn map-btn"
                              onClick={() => handleShowMapForMessage(message.content)}
                              title="Show on map"
                              style={{
                                background: 'linear-gradient(135deg, var(--accent-color), var(--accent-hover))',
                                border: '1px solid var(--accent-color)',
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: '0',
                                color: 'white',
                                transition: 'var(--transition-bounce, all 0.3s ease)'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className="feedback-btn"
                            onClick={() => handleFeedback(message.id, 'thumbs_up')}
                            title="Helpful response"
                            style={{
                              background: messageFeedback[message.id] === 'thumbs_up' ? 'var(--accent-color)' : 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: '0',
                              color: messageFeedback[message.id] === 'thumbs_up' ? 'white' : 'var(--text-tertiary)',
                              transition: 'all 0.2s'
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M7 22V11M2 13v7a2 2 0 0 0 2 2h12.764a2 2 0 0 0 1.789-1.106l2.894-5.789A2 2 0 0 0 19.658 12H16a2 2 0 0 1-2-2V5a3 3 0 0 0-3-3v0a2 2 0 0 0-2 2v4H4a2 2 0 0 0-2 2v0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button
                            className="feedback-btn"
                            onClick={() => handleFeedback(message.id, 'thumbs_down')}
                            title="Not helpful"
                            style={{
                              background: messageFeedback[message.id] === 'thumbs_down' ? '#ef4444' : 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: '0',
                              color: messageFeedback[message.id] === 'thumbs_down' ? 'white' : 'var(--text-tertiary)',
                              transition: 'all 0.2s'
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M17 2v11M22 11V4a2 2 0 0 0-2-2H7.236a2 2 0 0 0-1.789 1.106L2.553 8.894A2 2 0 0 0 4.342 12H8a2 2 0 0 1 2 2v5a3 3 0 0 0 3 3v0a2 2 0 0 0 2-2v-4h5a2 2 0 0 0 2-2v0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              ))}

              {/* Sign-in prompt after soft limit - HIDDEN */}
              {false && showSignInPrompt && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 152, 0, 0.1))',
                  border: '1px solid rgba(255, 193, 7, 0.5)',
                  borderRadius: '12px',
                  padding: '1rem',
                  margin: '1rem 0',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontWeight: '600' }}>
                    ⚠️ {HARD_LIMIT - anonymousMessageCount} messages remaining
                  </p>
                  <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    Sign in now to continue chatting without limits and save your conversation!
                  </p>
                  <button
                    onClick={() => navigate('/sign-in')}
                    style={{
                      padding: '0.5rem 1.5rem',
                      borderRadius: '3rem',
                      background: 'linear-gradient(135deg, var(--accent-color), var(--accent-hover))',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Sign In Now
                  </button>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input for Anonymous Users */}
        <div className="chat-input-container">
          <div className="chat-input-flat">
            {uploadingFile && (
              <div className="file-upload-status inside-input">
                <div className={`file-icon ${uploadingFile.type}`}>
                  {uploadingFile.type === 'pdf' ? '📄' : uploadingFile.type === 'doc' ? '📝' : '📎'}
                </div>
                <div className="file-info">
                  <span className="file-name">{uploadingFile.name}</span>
                  <span className={`file-status${uploadingFile.status === 'analyzing' ? ' rotating' : ''}`}>
                    {uploadingFile.status === 'done' ? 'Done ✓' : getAnalysisLabel()}
                  </span>
                </div>
                <div className="file-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              </div>
            )}
            {attachedFile && (
              <div className="attached-file-chip" title={attachedFile.name}>
                <span className="chip-icon">
                  {attachedFile.type === 'pdf' ? '📄' : attachedFile.type === 'doc' ? '📝' : '📎'}
                </span>
                <span className="chip-name">{attachedFile.name}</span>
                <button className="chip-remove" onClick={() => setAttachedFile(null)} title="Remove">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}
            <div className="flat-textarea-row">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt,.md"
              />
              <textarea
                ref={textareaRef}
                className="chat-input"
                placeholder={anonymousMessageCount >= HARD_LIMIT
                  ? "Sign in to continue chatting..."
                  : t('messagePlaceholder')}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
                disabled={sending || anonymousMessageCount >= HARD_LIMIT}
                spellCheck="true"
                autoComplete="on"
              />
              {input.trim() && (
                <button className="clear-input-btn" onClick={handleClearInput} title="Clear">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
            <div className="flat-actions-row">
              <button
                className="flat-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Upload file"
                disabled={sending}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="flat-actions-right">
                {!sending && (
                  <VoiceInput
                    onTranscriptionComplete={handleVoiceTranscription}
                    language="auto"
                    contextType="healthcare"
                  />
                )}
                {sending ? (
                  <div className="input-spinner" title="Responding..." />
                ) : (
                  <button
                    className="send-btn-flat"
                    onClick={handleSend}
                    disabled={(!input.trim() && !attachedFile) || sending || anonymousMessageCount >= HARD_LIMIT}
                    title="Send message"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
          <p className="ai-disclaimer">AI-generated. Always verify health information with a qualified professional.</p>
        </div>

        {/* Map Panel for Anonymous Users */}
        <MapPanel
          isOpen={showMapPanel}
          onClose={() => setShowMapPanel(false)}
          locationData={mapLocationData}
          searchQuery={mapSearchQuery}
        />
        <VoiceConversation
          currentChat={currentChat}
          isAuthenticated={!!user}
          onLocationDetected={checkForLocationAndShowMap}
          userName={user?.firstName || user?.fullName || user?.username}
        />
      </div>
    );
  }

  // Authenticated User View - Full Chat Interface
  // If no chats exist, one will be auto-created above
  return (
    <div className={chatShellClassName}>
      {/* Authenticated Chat Topbar */}
      <div className="chat-auth-bar">
        <button
          className="chat-hamburger-btn"
          onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
          title="Toggle sidebar"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="topbar-user-info">
          <span className="topbar-user-name">
            {user?.firstName && user?.lastName
              ? `${user.firstName} ${user.lastName}`
              : user?.firstName || user?.username || 'User'}
          </span>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="chat-messages" onScroll={handleScroll}>
        {loadingMessages ? (
          <div className="loading-messages" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <ChatGreeting onSuggestionClick={handleSuggestionClick} />
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message-wrapper ${message.role === 'user' ? 'user-message' : 'bot-message'}`}
            >
              {message.role === 'assistant' && (
                <div className={`message-avatar bot-avatar tuliza-avatar${message.id === typingMessageId ? ' responding' : ''}`}>
                  <img src="/tuliza.jpg" alt="Tuliza" className="tuliza-avatar-img" />
                </div>
              )}

              {/* Hide bubble when waiting for first chunk, show only avatar with spinner */}
              {!(message.id === typingMessageId && !displayedText) && (
              <div className="message-content">
                <div className={`message-bubble ${message.role === 'user' ? 'user-bubble' : 'bot-bubble'}`}>
                  {message.role === 'assistant' ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: message.id === typingMessageId ? displayedText : formatMessageContent(message.content)
                        }}
                      />
                  ) : editingMessageId === message.id ? (
                    <div className="edit-message-container">
                      <textarea
                        className="edit-message-textarea"
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSaveEdit(message.id);
                          }
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        autoFocus
                      />
                      <div className="edit-message-actions">
                        <button className="edit-cancel-btn" onClick={handleCancelEdit}>
                          {t('cancel')}
                        </button>
                        <button className="edit-save-btn" onClick={() => handleSaveEdit(message.id)}>
                          Send
                        </button>
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const hasDoc = message.content && message.content.startsWith('📎 ');
                      if (hasDoc) {
                        const lines = message.content.split('\n');
                        const docName = lines[0].replace('📎 ', '');
                        const rest = lines.slice(1).join('\n').trim();
                        return (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div className="msg-doc-badge" title={docName}>
                              <span className="badge-icon">📎</span>
                              <span className="badge-name">{docName}</span>
                            </div>
                            {rest && <div style={{ flex: 1, minWidth: '150px' }}>{renderUserText(message, rest)}</div>}
                          </div>
                        );
                      }
                      return renderUserText(message);
                    })()
                  )}
                </div>
                <div className="message-footer">
                  <div className="message-timestamp">
                    {formatTime(message.created_at)}
                    {message.role === 'user' && (
                      <span className="user-initials">
                        {user?.firstName ? (user.firstName[0] + (user.lastName ? user.lastName[0] : '')).toUpperCase() : 'ME'}
                      </span>
                    )}
                  </div>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopyMessage(message.content)}
                    title="Copy message"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      color: copiedMessageId === message.content ? 'var(--accent-color)' : 'var(--text-tertiary)',
                      transition: 'color 0.2s'
                    }}
                  >
                    {copiedMessageId === message.content ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                  {message.role === 'user' && editingMessageId !== message.id && (
                    <button
                      className="edit-btn"
                      onClick={() => handleEditMessage(message.id, message.content)}
                      title="Edit message"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        color: 'var(--text-tertiary)',
                        transition: 'color 0.2s'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  {message.role === 'assistant' && message.id !== typingMessageId && (
                    <>
                      {(() => {
                        const msgIndex = messages.findIndex(m => m.id === message.id);
                        const prevUserMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;
                        const userAsked = prevUserMsg && prevUserMsg.role === 'user' && shouldShowMap(prevUserMsg.content);
                        const responseHasPostcode = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i.test(message.content || '');
                        return userAsked || responseHasPostcode;
                      })() && (
                        <button
                          className="feedback-btn map-btn"
                          onClick={() => handleShowMapForMessage(message.content)}
                          title="Show on map"
                          style={{
                            background: 'linear-gradient(135deg, var(--accent-color), var(--accent-hover))',
                            border: '1px solid var(--accent-color)',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '0',
                            color: 'white',
                            transition: 'var(--transition-bounce, all 0.3s ease)'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                      )}
                      <button
                        className={`feedback-btn ${messageFeedback[message.id] === 'thumbs_up' ? 'active positive' : ''}`}
                        onClick={() => handleFeedback(message.id, 'thumbs_up')}
                        title="Helpful response"
                        style={{
                          background: messageFeedback[message.id] === 'thumbs_up' ? 'var(--accent-color)' : 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: '0',
                          color: messageFeedback[message.id] === 'thumbs_up' ? 'white' : 'var(--text-tertiary)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M7 22V11M2 13v7a2 2 0 0 0 2 2h12.764a2 2 0 0 0 1.789-1.106l2.894-5.789A2 2 0 0 0 19.658 12H16a2 2 0 0 1-2-2V5a3 3 0 0 0-3-3v0a2 2 0 0 0-2 2v4H4a2 2 0 0 0-2 2v0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        className={`feedback-btn ${messageFeedback[message.id] === 'thumbs_down' ? 'active negative' : ''}`}
                        onClick={() => handleFeedback(message.id, 'thumbs_down')}
                        title="Not helpful"
                        style={{
                          background: messageFeedback[message.id] === 'thumbs_down' ? '#ef4444' : 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: '0',
                          color: messageFeedback[message.id] === 'thumbs_down' ? 'white' : 'var(--text-tertiary)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M17 2v11M22 11V4a2 2 0 0 0-2-2H7.236a2 2 0 0 0-1.789 1.106L2.553 8.894A2 2 0 0 0 4.342 12H8a2 2 0 0 1 2 2v5a3 3 0 0 0 3 3v0a2 2 0 0 0 2-2v-4h5a2 2 0 0 0 2-2v0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      <div className="chat-input-container">
        <div className="chat-input-flat">
          {uploadingFile && (
            <div className="file-upload-status inside-input">
              <div className={`file-icon ${uploadingFile.type}`}>
                {uploadingFile.type === 'pdf' ? '📄' : uploadingFile.type === 'doc' ? '📝' : '📎'}
              </div>
              <div className="file-info">
                <span className="file-name">{uploadingFile.name}</span>
                <span className={`file-status${uploadingFile.status === 'analyzing' ? ' rotating' : ''}`}>
                  {uploadingFile.status === 'done' ? 'Done ✓' : getAnalysisLabel()}
                </span>
              </div>
              <div className="file-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            </div>
          )}
          {attachedFile && (
            <div className="attached-file-chip" title={attachedFile.name}>
              <span className="chip-icon">
                {attachedFile.type === 'pdf' ? '📄' : attachedFile.type === 'doc' ? '📝' : '📎'}
              </span>
              <span className="chip-name">{attachedFile.name}</span>
              <button className="chip-remove" onClick={() => setAttachedFile(null)} title="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}
          <div className="flat-textarea-row">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.md"
            />
            <textarea
              ref={textareaRef}
              className="chat-input"
              placeholder={t('messagePlaceholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
              disabled={sending}
              spellCheck="true"
              autoComplete="on"
            />
            {input.trim() && (
              <button className="clear-input-btn" onClick={handleClearInput} title="Clear">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
          <div className="flat-actions-row">
            <button
              className="flat-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Upload file"
              disabled={sending}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="flat-actions-right">
              {!sending && (
                <VoiceInput
                  onTranscriptionComplete={handleVoiceTranscription}
                  language="auto"
                  contextType="healthcare"
                />
              )}
              {sending ? (
                <div className="input-spinner" title="Responding..." />
              ) : (
                <button
                  className="send-btn-flat"
                  onClick={handleSend}
                  disabled={(!input.trim() && !attachedFile) || sending}
                  title="Send message"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <p className="ai-disclaimer">AI-generated. Always verify health information with a qualified professional.</p>
      </div>

      <MapPanel
        isOpen={showMapPanel}
        onClose={() => setShowMapPanel(false)}
        locationData={mapLocationData}
        searchQuery={mapSearchQuery}
      />
      <VoiceConversation
        currentChat={currentChat}
        isAuthenticated={!!user}
        onLocationDetected={checkForLocationAndShowMap}
        userName={user?.firstName || user?.fullName || user?.username}
      />
    </div>
  );
};

export default Chat;
